import type { TPropertyOption } from 'App/dashboards/_utils/useDashboardPropertyOptions'
import { useMemo } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { checkAuthorization } from 'utils/checkAuthorization'
import { sortByKey } from 'utils/sortByKey'

// Whether the user can see irrigation activity for a property — the gate for the water dashboards'
// property list (mirrors hasPestDataAccess, but for the water family's primary permission).
export const hasIrrigationAccess = (propertyId: number): boolean =>
  checkAuthorization({ permission: 'VIEW_IRRIGATION_DETAILS', entity: propertyId })

// Multi-property option list for the water dashboards: every property the user has irrigation access
// to that has something the dashboard shows. Standalone (not the pest-gated DashboardFiltersContext)
// so the two families stay independent.
export const useWaterPropertyOptions = (hasAssets: (property: TProperty) => boolean): TPropertyOption[] => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  // Recompute once permissions load, not just when properties do (read via checkAuthorization below).
  const permissions = userDetailsStore.useSelector((s) => s.permissions)

  return useMemo<TPropertyOption[]>(() => {
    return Object.entries(properties || {})
      .filter(([propertyId, p]) => hasIrrigationAccess(Number(propertyId)) && hasAssets(p))
      .map(([propertyId, p]) => ({ value: propertyId, label: p.propertyName, group: p.organizationName }))
      .sort(sortByKey('label'))
    // `permissions` is read inside hasIrrigationAccess via checkAuthorization rather than directly.
  }, [properties, permissions, hasAssets])
}
